import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';

export const SecurityPage: React.FC = () => {
  const { t } = useTranslation();

  const securityFeatures = [
    {
      icon: 'fas fa-shield-alt',
      title: 'Smart Contract Security',
      description: 'Our smart contracts undergo rigorous security audits to ensure code integrity and vulnerability protection.',
      features: [
        'Multiple independent security audits',
        'Bug bounty programs with responsible disclosure',
        'Formal verification of critical functions',
        'Regular security updates and patches'
      ]
    },
    {
      icon: 'fas fa-lock',
      title: 'Data Protection',
      description: 'We employ advanced encryption and security measures to protect user data and communications.',
      features: [
        'End-to-end encryption for all communications',
        'Secure data storage with encryption at rest',
        'Multi-factor authentication for sensitive operations',
        'Regular security assessments and penetration testing'
      ]
    },
    {
      icon: 'fas fa-server',
      title: 'Infrastructure Security',
      description: 'Our infrastructure is designed with security as a top priority, with multiple layers of protection.',
      features: [
        'Secure hosting with DDoS protection',
        'Regular security monitoring and threat detection',
        'Incident response procedures',
        'Disaster recovery and business continuity plans'
      ]
    }
  ];

  const bestPractices = [
    {
      icon: 'fas fa-wallet',
      title: 'Secure Your Wallet',
      description: 'Use a reputable wallet and keep your private keys secure. Never share your private keys or seed phrases with anyone.',
      color: 'from-blue-500/20 to-blue-500/5'
    },
    {
      icon: 'fas fa-key',
      title: 'Use Strong Passwords',
      description: 'Create strong, unique passwords for your IDNGOLD account and enable two-factor authentication when available.',
      color: 'from-green-500/20 to-green-500/5'
    },
    {
      icon: 'fas fa-user-shield',
      title: 'Beware of Phishing',
      description: 'Always verify you\'re on the official IDNGOLD website before entering your credentials. Be suspicious of unsolicited emails.',
      color: 'from-red-500/20 to-red-500/5'
    },
    {
      icon: 'fas fa-sync',
      title: 'Keep Software Updated',
      description: 'Keep your browser, wallet software, and operating system up to date with the latest security patches.',
      color: 'from-purple-500/20 to-purple-500/5'
    }
  ];

  const audits = [
    {
      name: 'Certik Audit',
      description: 'Smart contract security audit completed with no critical vulnerabilities.',
      status: 'Completed',
      date: '2025 Q1',
      badge: 'No Critical Issues'
    },
    {
      name: 'ConsenSys Diligence',
      description: 'Comprehensive security assessment of our platform infrastructure.',
      status: 'Completed',
      date: '2025 Q1',
      badge: 'Security Verified'
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.security.title')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight">
            Enterprise-Grade Security
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Protecting your digital gold assets with industry-leading security measures and protocols
          </p>
        </div>
      </section>

      {/* Security Features */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.security.platform.title')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('pages.security.platform.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              IDNGOLD implements industry-leading security measures to protect user assets and maintain platform integrity
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="modern-card group">
                <div className="feature-icon">
                  <i className={`${feature.icon} text-3xl text-gold group-hover:scale-110 transition-transform`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  {feature.description}
                </p>
                <ul className="space-y-3">
                  {feature.features.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.security.bestPractices.title')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('pages.security.bestPractices.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Follow these best practices to keep your account and assets secure
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {bestPractices.map((practice, index) => (
              <div key={index} className="glass-card text-center group">
                <div className={`w-20 h-20 bg-gradient-to-br ${practice.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform`}>
                  <i className={`${practice.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                  {practice.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {practice.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Audits */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.security.audits.title')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('pages.security.audits.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Our platform and smart contracts have been audited by leading security firms
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {audits.map((audit, index) => (
              <div key={index} className="partner-card group">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                    <i className="fas fa-shield-alt text-5xl text-green-400"></i>
                  </div>
                  <div className="inline-block px-3 py-1 bg-green-500/20 rounded-full mb-4">
                    <span className="text-green-400 text-xs font-semibold uppercase tracking-wider">{audit.status}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                    {audit.name}
                  </h3>
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {audit.description}
                  </p>
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <div className="text-sm text-gray-500">
                      <i className="fas fa-calendar mr-2"></i>
                      {audit.date}
                    </div>
                    <div className="inline-block px-3 py-1 bg-gold/20 rounded-full">
                      <span className="text-gold text-xs font-semibold">{audit.badge}</span>
                    </div>
                  </div>
                  <Button variant="secondary" size="sm" className="group">
                    <span className="flex items-center">
                      View Report
                      <i className="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform"></i>
                    </span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vulnerability Reporting */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="cta-section relative border-2 border-yellow-500/20">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-500/20 to-yellow-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-bug text-4xl text-yellow-400"></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-6">{t('pages.security.report.title')}</h2>
                <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                  {t('pages.security.report.description')}
                </p>

                <div className="glass-card text-left mb-8">
                  <h3 className="text-xl font-bold text-yellow-400 mb-4">
                    <i className="fas fa-exclamation-triangle mr-3"></i>
                    {t('pages.security.report.policyTitle')}
                  </h3>
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {t('pages.security.report.policyDescription')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href={`mailto:${t('pages.security.report.email')}`}
                      className="group relative overflow-hidden px-6 py-3 text-lg font-bold bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-full hover:from-yellow-600 hover:to-yellow-500 transition-all duration-300 text-center"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        <i className="fas fa-envelope mr-3"></i>
                        {t('pages.security.report.action')}
                      </span>
                    </a>
                    <Link to="/resources">
                      <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                        View Security Guidelines
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">Trust & Compliance</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">Built on Trust</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Your security is our top priority
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-check-circle text-3xl text-green-400"></i>
              </div>
              <div className="text-2xl font-black text-gold mb-2">100%</div>
              <div className="text-sm text-gray-400">Audited</div>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-shield-alt text-3xl text-blue-400"></i>
              </div>
              <div className="text-2xl font-black text-gold mb-2">24/7</div>
              <div className="text-sm text-gray-400">Monitoring</div>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-lock text-3xl text-purple-400"></i>
              </div>
              <div className="text-2xl font-black text-gold mb-2">256-bit</div>
              <div className="text-sm text-gray-400">Encryption</div>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-red-500/20 to-red-500/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-user-shield text-3xl text-red-400"></i>
              </div>
              <div className="text-2xl font-black text-gold mb-2">KYC</div>
              <div className="text-sm text-gray-400">Verified</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};